import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/components/theme-provider";
import { TenantConfigProvider } from "@/hooks/useTenantConfig";
import { I18nProvider } from "@/hooks/useI18n";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ProtectedModule } from "@/components/ProtectedModule";
import { CompanySecurityWrapper } from "@/components/CompanySecurityWrapper";
import { PasswordChangeGuard } from "@/components/auth/PasswordChangeGuard";
import { BusinessOperationsProvider } from "./contexts/BusinessOperationsContext";
import { DataProvider } from "@/contexts/DataContext";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorBoundary from "@/components/ErrorBoundary";
import { DevPerformanceMonitor } from "@/components/monitoring/PerformanceMonitor";
import { useGlobalErrorHandler } from "@/hooks/useGlobalErrorHandler";
import { QueryDebugger } from "@/components/debug/QueryDebugger";

// Lazy import helper with retry for transient chunk 404s
function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries: number = 2,
  delayMs: number = 800
) {
  return lazy(() =>
    new Promise<any>((resolve, reject) => {
      const attempt = (n: number) => {
        factory()
          .then(resolve)
          .catch((err) => {
            const msg = String(err?.message || err);
            const isChunkError =
              msg.includes('Failed to fetch dynamically imported module') ||
              msg.includes('Importing a module script failed');
            if (isChunkError && n > 0) {
              setTimeout(() => attempt(n - 1), delayMs);
            } else if (isChunkError) {
              // Last resort: cache-bust reload to get fresh chunk map
              const url = new URL(window.location.href);
              url.searchParams.set('v', Date.now().toString());
              window.location.replace(url.toString());
              reject(err);
            } else {
              reject(err);
            }
          });
      };
      attempt(retries);
    })
  );
}

// Critical routes - loaded immediately (only auth and dashboard)
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";

// Admin routes - lazy loaded (not needed on initial page)
const CompaniesModule = lazyWithRetry(() => import("./pages/CompaniesModule"));
const BranchesModule = lazyWithRetry(() => import("./pages/BranchesModule"));
const ModularisLanding = lazy(() => import("./pages/ModularisLandingNew"));

// Non-critical routes - lazy loaded with retry on chunk errors
const UsersModule = lazyWithRetry(() => import("./pages/UsersModule"));
const RolesModule = lazyWithRetry(() => import("./pages/RolesModule"));
const ProductsModule = lazyWithRetry(() => import("./pages/ProductsModule"));
const SalesModule = lazy(() => import("./pages/SalesModule"));
const PublicSaleView = lazy(() => import("./pages/PublicSaleView"));
const PublicLeadForm = lazy(() => import("./pages/PublicLeadForm"));
const SharedOpportunity = lazy(() => import("./pages/SharedOpportunity"));
const PurchasesModule = lazy(() => import("./pages/PurchasesModule"));
const CustomersModule = lazy(() => import("./pages/CustomersModule"));
const TransfersModule = lazy(() => import("./pages/TransfersModule"));
const InventoryMovementsModule = lazy(() => import("./pages/InventoryMovementsModule"));
const ReportsModule = lazy(() => import("./pages/ReportsModule"));
const ReportsHub = lazy(() => import("./pages/ReportsHub"));
const ReportsSalesModule = lazy(() => import("./pages/ReportsSalesModule"));
const ReportsInventoryModule = lazy(() => import("./pages/ReportsInventoryModule"));
const ReportsPurchasesModule = lazy(() => import("./pages/ReportsPurchasesModule"));
const ReportsScheduledModule = lazy(() => import("./pages/ReportsScheduledModule"));
const HelpModule = lazy(() => import("./pages/HelpModule"));
const UserManualModule = lazy(() => import("./pages/UserManualModule"));
const DesignSystemDemo = lazy(() => import("./pages/DesignSystemDemo"));

// Lazy load ConsignmentDropshippingModule for better initial bundle size
const ConsignmentDropshippingModule = lazy(() => import("./pages/ConsignmentDropshippingModule"));
const B2BShowroomModule = lazy(() => import("./pages/B2BShowroomModule"));
const AccountingModule = lazy(() => import("./pages/AccountingModule"));
const ConfigurationModule = lazy(() => import("./pages/ConfigurationModule"));
const ImplementationModule = lazy(() => import("./pages/ImplementationModule"));
const HumanResourcesModule = lazy(() => import("./pages/HumanResourcesModule"));
const TasksModule = lazy(() => import("./pages/TasksModule"));
const ServiceTechModule = lazy(() => import("./components/admin/ServiceTechManager").then(module => ({ default: module.ServiceTechManager })));
const UserFinesModule = lazy(() => import("./pages/UserFinesModule"));
const SiteMapModule = lazy(() => import("./pages/SiteMapModule"));
const SystemModulesView = lazy(() => import("@/components/admin/SystemModulesView"));
const InterCompanyConfig = lazy(() => import("./pages/admin/InterCompanyConfig"));

const CorporateCatalogDemo = lazy(() => import("./pages/CorporateCatalogDemo"));
const CorporateContacts = lazy(() => import("./pages/CorporateContacts"));
const CashControlModule = lazy(() => import("./pages/CashControlModule"));
const CashClosureTracking = lazy(() => import("./pages/CashClosureTracking"));
const InvoiceTestPage = lazy(() => import('./components/invoice/InvoiceTestPage').then(module => ({ default: module.InvoiceTestPage })));

// Lazy load heavy components
const ModulePermissionsManager = lazy(() => import("@/components/admin/ModulePermissionsManager").then(module => ({ default: module.ModulePermissionsManager })));
const ProductDecompositionManager = lazy(() => import('./components/inventory/ProductDecompositionManager'));
const POSViewCompact = lazy(() => import('./components/sales/POSViewCompact').then(module => ({ default: module.POSViewCompact })));
const QuotesView = lazy(() => import('./components/sales/QuotesView').then(module => ({ default: module.QuotesView })));
const SalesView = lazy(() => import('./components/sales/SalesView'));
const DigifactDiagnostic = lazy(() => import('./components/digifact/DigifactDiagnostic'));
const CashManagementView = lazy(() => import('./components/sales/CashManagementView'));
const SaasAdminModule = lazy(() => import('./pages/SaasAdminModule'));
const UserRoleStatusPage = lazy(() => import('./pages/UserRoleStatusPage'));
const PettyCashView = lazy(() => import('./components/sales/PettyCashView').then(module => ({ default: module.PettyCashView })));
const CommissionsView = lazy(() => import('./components/sales/CommissionsView').then(module => ({ default: module.CommissionsView })));
const DisciplinaryView = lazy(() => import('./components/admin/DisciplinaryView').then(module => ({ default: module.DisciplinaryView })));
const PerformanceDashboard = lazy(() => import('./components/sales/PerformanceDashboard'));
const AdvanceOrdersView = lazy(() => import('./components/sales/AdvanceOrdersView'));
const StockView = lazy(() => import('./components/inventory/StockView').then(module => ({ default: module.StockView })));
const ProductKardexView = lazy(() => import('./components/inventory/ProductKardexView').then(module => ({ default: module.ProductKardexView })));
const PriceListsView = lazy(() => import('./components/inventory/PriceListsView').then(module => ({ default: module.PriceListsView })));
const CategoriesView = lazy(() => import('./components/inventory/CategoriesView').then(module => ({ default: module.CategoriesView })));
const BrandsView = lazy(() => import('./components/inventory/BrandsView').then(module => ({ default: module.BrandsView })));
const InventoryByLocationView = lazy(() => import('./components/inventory/InventoryByLocationView').then(module => ({ default: module.InventoryByLocationView })));
const StockOperationsView = lazy(() => import('./components/inventory/StockOperationsView').then(module => ({ default: module.StockOperationsView })));
const InventoryAdjustmentsView = lazy(() => import('./components/inventory/InventoryAdjustmentsView'));
const PurchaseOrdersView = lazy(() => import('./components/inventory/PurchaseOrdersView').then(module => ({ default: module.PurchaseOrdersView })));
const UnifiedSuppliersView = lazy(() => import('./components/inventory/UnifiedSuppliersView').then(module => ({ default: module.UnifiedSuppliersView })));
const PurchaseReportsView = lazy(() => import('./components/inventory/PurchaseReportsView').then(module => ({ default: module.PurchaseReportsView })));
const WarehousesView = lazy(() => import('./components/inventory/WarehousesView').then(module => ({ default: module.WarehousesView })));
const WarehouseLocationManager = lazy(() => import('./components/inventory/WarehouseLocationManager').then(module => ({ default: module.WarehouseLocationManager })));
const ProductTemplatesView = lazy(() => import('./components/inventory/ProductTemplatesView').then(module => ({ default: module.ProductTemplatesView })));
const SalesTargetsWithCarryoverView = lazy(() => import('./components/sales/SalesTargetsWithCarryoverView').then(module => ({ default: module.SalesTargetsWithCarryoverView })));
const UnifiedProductsView = lazy(() => import('./components/inventory/UnifiedProductsView').then(module => ({ default: module.UnifiedProductsView })));
const ShowroomManagement = lazy(() => import('./components/b2b/ShowroomManagement').then(module => ({ default: module.ShowroomManagement })));
const ShowroomProductCatalog = lazy(() => import('./components/b2b/ShowroomProductCatalog').then(module => ({ default: module.ShowroomProductCatalog })));
const ShowroomCustomerAccess = lazy(() => import('./components/b2b/ShowroomCustomerAccess').then(module => ({ default: module.ShowroomCustomerAccess })));
const DynamicConfigPanel = lazy(() => import('./components/business/DynamicConfigPanel').then(module => ({ default: module.DynamicConfigPanel })));
const MassProductGeneration = lazy(() => import('./pages/MassProductGeneration').then(module => ({ default: module.MassProductGeneration })));
const CodeGenerator = lazy(() => import('./pages/CodeGenerator').then(module => ({ default: module.default })));
const PredefinedTexts = lazy(() => import('./pages/PredefinedTexts').then(module => ({ default: module.default })));
const PriceMappingPage = lazy(() => import('./pages/PriceMappingPage').then(module => ({ default: module.PriceMappingPage })));
const NewPriceMappingSessionPage = lazy(() => import('./pages/NewPriceMappingSessionPage').then(module => ({ default: module.NewPriceMappingSessionPage })));
const PriceMappingSessionDetailsPage = lazy(() => import('./pages/PriceMappingSessionDetailsPage').then(module => ({ default: module.PriceMappingSessionDetailsPage })));

// Consignment and Dropshipping components
const ConsignmentManagementView = lazy(() => import('./components/consignment/ConsignmentManagementView'));
const ConsignmentReceptionView = lazy(() => import('./components/consignment/ConsignmentReceptionView').then(module => ({ default: module.ConsignmentReceptionView })));
const ConsignmentStockView = lazy(() => import('./components/consignment/ConsignmentStockView').then(module => ({ default: module.ConsignmentStockView })));
const ConsignmentMovementsView = lazy(() => import('./components/consignment/ConsignmentMovementsView').then(module => ({ default: module.ConsignmentMovementsView })));
const ConsignmentReconciliationView = lazy(() => import('./components/consignment/ConsignmentReconciliationView').then(module => ({ default: module.ConsignmentReconciliationView })));
const ProviderStockDistributionView = lazy(() => import('./components/consignment/ProviderStockDistributionView').then(module => ({ default: module.ProviderStockDistributionView })));
const DropshippingDashboardView = lazy(() => import('./components/dropshipping/DropshippingDashboardView').then(module => ({ default: module.DropshippingDashboardView })));
const SmartDashboard = lazy(() => import('./components/smart/SmartDashboard').then(module => ({ default: module.SmartDashboard })));
const DropshippingSuppliersView = lazy(() => import('./components/dropshipping/DropshippingSuppliersView').then(module => ({ default: module.DropshippingSuppliersView })));
const DropshippingProductsView = lazy(() => import('./components/dropshipping/DropshippingProductsView').then(module => ({ default: module.DropshippingProductsView })));

// CRM Module
const CRMView = lazy(() => import('./pages/CRMView'));
const DropshippingOrdersView = lazy(() => import('./components/dropshipping/DropshippingOrdersView').then(module => ({ default: module.DropshippingOrdersView })));
const DropshippingConfigurationManager = lazy(() => import('./components/dropshipping/DropshippingConfigurationManager').then(module => ({ default: module.DropshippingConfigurationManager })));
const DropshippingProviderMovements = lazy(() => import('./components/dropshipping/DropshippingProviderMovements').then(module => ({ default: module.DropshippingProviderMovements })));
const AutomaticReplenishmentSystem = lazy(() => import('./components/logistics/AutomaticReplenishmentSystem').then(module => ({ default: module.AutomaticReplenishmentSystem })));
const ConsignmentObsolescenceAnalysis = lazy(() => import('./components/logistics/ConsignmentObsolescenceAnalysis').then(module => ({ default: module.ConsignmentObsolescenceAnalysis })));
const InterCompanyConsignmentFlow = lazy(() => import('./components/logistics/InterCompanyConsignmentFlow').then(module => ({ default: module.InterCompanyConsignmentFlow })));
const SystemPerformanceAnalyzer = lazy(() => import('./pages/SystemPerformanceAnalyzer'));

// Client Support Tickets component
const ClientSupportTickets = lazy(() => import('./components/support/ClientSupportTickets').then(module => ({ default: module.ClientSupportTickets })));

// Customer Cases Management
const CustomerCasesPage = lazy(() => import('./pages/CustomerCasesPage').then(module => ({ default: module.CustomerCasesPage })));

// Image Processor Demo
const ImageProcessorDemo = lazy(() => import('./components/ImageProcessorDemo').then(module => ({ default: module.ImageProcessorDemo })));
const ProductVisualGenerator = lazy(() => import('./pages/ProductVisualGenerator').then(module => ({ default: module.default })));

// Loading fallback component
const ModuleLoader = () => (
  <div className="p-6 space-y-4">
    <Skeleton className="h-8 w-48" />
    <Skeleton className="h-64 w-full" />
  </div>
);

const queryClient = new QueryClient();

const App = () => {
  // Activate global error handling for chunk/network errors
  useGlobalErrorHandler();
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="system" storageKey="lovable-ui-theme">
          <AuthProvider>
            <TenantConfigProvider>
              <I18nProvider>
                <BusinessOperationsProvider>
              <DataProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <DevPerformanceMonitor />
                  <QueryDebugger />
                  <BrowserRouter>
                    <CompanySecurityWrapper>
                      <PasswordChangeGuard>
                        <Routes>
                      {/* Public routes */}
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/public/sale/:token" element={
                        <Suspense fallback={<ModuleLoader />}>
                          <PublicSaleView />
                        </Suspense>
                      } />
                      <Route path="/lead/:slug" element={
                        <Suspense fallback={<ModuleLoader />}>
                          <PublicLeadForm />
                        </Suspense>
                      } />
                      <Route path="/shared/opportunity/:token" element={
                        <Suspense fallback={<ModuleLoader />}>
                          <SharedOpportunity />
                        </Suspense>
                      } />
                      
                      {/* Landing page as root */}
                      <Route path="/" element={
                        <Suspense fallback={<ModuleLoader />}>
                          <ModularisLanding />
                        </Suspense>
                      } />
                      <Route path="/modularis" element={
                        <Suspense fallback={<ModuleLoader />}>
                          <ModularisLanding />
                        </Suspense>
                      } />
                      
                      {/* Protected routes with Layout */}
                      <Route path="/dashboard" element={
                        <Layout>
                          <Dashboard />
                        </Layout>
                      } />
                      <Route path="/users" element={
                        <Layout>
                          <ProtectedRoute permission="manage_users">
                            <Suspense fallback={<ModuleLoader />}>
                              <UsersModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/companies" element={
                        <Layout>
                          <Suspense fallback={<ModuleLoader />}>
                            <CompaniesModule />
                          </Suspense>
                        </Layout>
                      } />
                      <Route path="/branches" element={
                        <Layout>
                          <Suspense fallback={<ModuleLoader />}>
                            <BranchesModule />
                          </Suspense>
                        </Layout>
                      } />
                      <Route path="/roles" element={
                        <Layout>
                          <ProtectedRoute permission="manage_roles">
                            <Suspense fallback={<ModuleLoader />}>
                              <RolesModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Admin roles route */}
                      <Route path="/admin/roles" element={
                        <Layout>
                          <ProtectedRoute permission="manage_roles">
                            <Suspense fallback={<ModuleLoader />}>
                              <RolesModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/configuration" element={
                        <Layout>
                          <ProtectedRoute permission="manage_configuration">
                            <Suspense fallback={<ModuleLoader />}>
                              <ConfigurationModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Admin modules route */}
                      <Route path="/admin/modules" element={
                        <Layout>
                          <ProtectedRoute permission="manage_configuration">
                            <Suspense fallback={<ModuleLoader />}>
                              <SystemModulesView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inter-company configuration route */}
                      <Route path="/admin/inter-company" element={
                        <Layout>
                          <ProtectedRoute requireSuperAdmin>
                            <Suspense fallback={<ModuleLoader />}>
                              <InterCompanyConfig />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Admin dashboard route */}
                      <Route path="/admin" element={
                        <Layout>
                          <ProtectedRoute permission="manage_configuration">
                            <Suspense fallback={<ModuleLoader />}>
                              <ConfigurationModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />

                      {/* Image Processor Demo - AI Image Processing */}
                      <Route path="/admin/image-processor" element={
                        <Layout>
                          <ProtectedRoute permission="manage_configuration">
                            <Suspense fallback={<ModuleLoader />}>
                              <ImageProcessorDemo />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />

                      {/* Product Visual Generator - AI Product Images */}
                      <Route path="/admin/product-visual-generator" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ProductVisualGenerator />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />

                       {/* SaaS Admin route - Only for Super Admins */}
                       <Route path="/saas-admin" element={
                         <Layout>
                           <ProtectedRoute requireSuperAdmin>
                             <Suspense fallback={<ModuleLoader />}>
                               <SaasAdminModule />
                             </Suspense>
                           </ProtectedRoute>
                         </Layout>
                       } />
                       
                       {/* User Role Status - Para que usuarios puedan ver sus permisos */}
                       <Route path="/user-role-status" element={
                         <Layout>
                           <Suspense fallback={<ModuleLoader />}>
                             <UserRoleStatusPage />
                           </Suspense>
                         </Layout>
                       } />
                      <Route path="/business-config" element={
                        <Layout>
                          <ProtectedRoute permission="manage_configuration">
                            <div className="p-6">
                              <Suspense fallback={<ModuleLoader />}>
                                <DynamicConfigPanel />
                              </Suspense>
                            </div>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Products routes */}
                      <Route path="/products/*" element={
                        <Layout>
                          <ProtectedRoute permission="view_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ProductsModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inventory Products route */}
                      <Route path="/inventory/*" element={
                        <Layout>
                          <ProtectedRoute permission="view_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ProductsModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/products/decomposition" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <div className="p-6">
                              <Suspense fallback={<ModuleLoader />}>
                                <ProductDecompositionManager />
                              </Suspense>
                            </div>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      

                      {/* Inventory Movements route */}
                      <Route path="/inventory/movements" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <InventoryMovementsModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Sales routes */}
                      <Route path="/sales/*" element={
                        <Layout>
                            <ProtectedRoute permission="view_sales">
                              <Suspense fallback={<ModuleLoader />}>
                                <SalesModule />
                              </Suspense>
                            </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Performance Dashboard route */}
                      <Route path="/sales/performance" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <PerformanceDashboard />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Cash Control routes */}
                      <Route path="/cash-control/*" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <CashControlModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* POS route */}
                      <Route path="/pos" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <POSViewCompact />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Sales POS route */}
                      <Route path="/sales/pos" element={
                        <Layout>
                          <ProtectedRoute permission="view_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <POSViewCompact />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      {/* Backward compatibility: pos-compact */}
                      <Route path="/sales/pos-compact" element={
                        <Layout>
                          <ProtectedRoute permission="view_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <POSViewCompact />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Cash Management route */}
                      <Route path="/cash-management" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <CashManagementView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Cash Closure Tracking route */}
                      <Route path="/cash-closure-tracking" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <CashClosureTracking />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Sales Cash Management route */}
                      <Route path="/sales/cash-management" element={
                        <Layout>
                          <Suspense fallback={<ModuleLoader />}>
                            <CashManagementView />
                          </Suspense>
                        </Layout>
                      } />
                      
                      {/* Sales Point of Sale route */}
                      <Route path="/sales/point-of-sale" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <POSViewCompact />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Sales Quotations route */}
                      <Route path="/sales/quotations" element={
                        <Layout>
                            <ProtectedRoute permission="view_sales">
                              <Suspense fallback={<ModuleLoader />}>
                                <SalesModule />
                              </Suspense>
                            </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Cotizaciones route */}
                      <Route path="/cotizaciones" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <QuotesView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Sales Cotizaciones route */}
                      <Route path="/sales/cotizaciones" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <QuotesView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Invoice Test Page */}
                      <Route path="/invoice-test" element={
                        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
                          <div className="p-4 sm:p-6 lg:p-8">
                            <InvoiceTestPage />
                          </div>
                        </div>
                      } />

                      {/* Sales Invoices route */}
                      <Route path="/sales/invoices" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <SalesView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Ventas y Facturas route */}
                      <Route path="/ventas-facturas" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <SalesView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Sales Ventas y Facturas route */}
                      <Route path="/sales/ventas-facturas" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <SalesView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Sales Ventas route */}
                      <Route path="/sales/ventas" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <SalesView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* CRM route */}
                      <Route path="/crm" element={
                        <Layout>
                          <ProtectedRoute permission="view_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <CRMView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Contactos Corporativos route */}
                      <Route path="/corporate-contacts" element={
                        <Layout>
                          <ProtectedRoute permission="view_corporate_contacts">
                            <CompanySecurityWrapper>
                              <Suspense fallback={<ModuleLoader />}>
                                <CorporateContacts />
                              </Suspense>
                            </CompanySecurityWrapper>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* DigiFact Diagnostic route */}
                      <Route path="/sales/digifact-diagnostic" element={
                        <Layout>
                            <ProtectedRoute requireSuperAdmin>
                              <Suspense fallback={<ModuleLoader />}>
                                <DigifactDiagnostic />
                              </Suspense>
                            </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Sales Advance Orders route */}
                      <Route path="/sales/advance-orders" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <AdvanceOrdersView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inventory Stock route */}
                      <Route path="/inventory/stock" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <StockView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inventory Stock Control route */}
                      <Route path="/inventory/stock-control" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <StockView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inventory Kardex route */}
                      <Route path="/inventory/kardex" element={
                        <ProtectedRoute permission="manage_inventory">
                          <Layout>
                            <Suspense fallback={<ModuleLoader />}>
                              <InventoryMovementsModule />
                            </Suspense>
                          </Layout>
                        </ProtectedRoute>
                      } />
                      
                      {/* Inventory Price Lists route */}
                      <Route path="/inventory/price-lists" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <PriceListsView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inventory Categories route */}
                      <Route path="/inventory/categories" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <CategoriesView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inventory Brands route */}
                      <Route path="/inventory/brands" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <BrandsView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inventory by Location route */}
                       <Route path="/inventory/by-location" element={
                         <Layout>
                           <ProtectedModule moduleCode="inventory_locations">
                             <Suspense fallback={<ModuleLoader />}>
                               <InventoryByLocationView />
                             </Suspense>
                           </ProtectedModule>
                         </Layout>
                       } />
                      
                      {/* Inventory Locations route (alias) */}
                       <Route path="/inventory/locations" element={
                         <Layout>
                           <ProtectedModule moduleCode="inventory_locations">
                             <Suspense fallback={<ModuleLoader />}>
                               <InventoryByLocationView />
                             </Suspense>
                           </ProtectedModule>
                         </Layout>
                       } />
                      
                      
                      {/* Inventory Stock Operations route */}
                      <Route path="/inventory/stock-operations" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <StockOperationsView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inventory Operations route (alias) */}
                      <Route path="/inventory/operations" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <StockOperationsView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inventory Adjustments route */}
                      <Route path="/inventory/adjustments" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <InventoryAdjustmentsView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Price Mapping Tool routes */}
                      <Route path="/inventory/price-mapping" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <PriceMappingPage />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      <Route path="/inventory/price-mapping/new" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <NewPriceMappingSessionPage />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      <Route path="/inventory/price-mapping/:sessionId" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <PriceMappingSessionDetailsPage />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Purchases Purchase Orders route */}
                      <Route path="/purchases/purchase-orders" element={
                        <Layout>
                          <ProtectedRoute permission="manage_purchases">
                            <Suspense fallback={<ModuleLoader />}>
                              <PurchaseOrdersView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Purchases Suppliers route */}
                      <Route path="/purchases/suppliers" element={
                        <Layout>
                          <ProtectedRoute permission="manage_purchases">
                            <Suspense fallback={<ModuleLoader />}>
                              <UnifiedSuppliersView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Purchases Reports route */}
                      <Route path="/purchases/reports" element={
                        <Layout>
                          <ProtectedRoute permission="manage_purchases">
                            <Suspense fallback={<ModuleLoader />}>
                              <PurchaseReportsView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Almacenes route */}
                      <Route path="/inventory/warehouses" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <WarehousesView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Generación Masiva route */}
                      <Route path="/inventory/mass-generation" element={
                        <Layout>
                          <ProtectedRoute permission="manage_mass_generation">
                            <Suspense fallback={<ModuleLoader />}>
                              <MassProductGeneration />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />

                      {/* Generador de Códigos QR y Barcode route */}
                      <Route path="/inventory/code-generator" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <CodeGenerator />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />

                      {/* Textos Predefinidos route */}
                      <Route path="/inventory/predefined-texts" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <PredefinedTexts />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />

                      {/* Warehouse Location Management route */}
                      <Route path="/inventory/warehouse-locations" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <WarehouseLocationManager />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Plantillas route */}
                      <Route path="/inventory/templates" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ProductTemplatesView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Metas de Ventas route */}
                      <Route path="/sales/targets" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <SalesTargetsWithCarryoverView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Metas con Arrastre route */}
                      <Route path="/sales/carryover-targets" element={
                        <Layout>
                          <ProtectedRoute permission="view_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <SalesTargetsWithCarryoverView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Comisiones route */}
                      <Route path="/sales/commissions" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <CommissionsView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      
                      {/* Cash Differences route */}
                      <Route path="/cash-differences" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <CashControlModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Cash Differences route removed - handled by nested SalesModule routing */}
                      
                      {/* Petty Cash route */}
                      <Route path="/petty-cash" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <PettyCashView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* User Fines route */}
                      <Route path="/user-fines" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <UserFinesModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Customers routes */}
                      <Route path="/customers" element={
                        <Layout>
                          <ProtectedRoute permission="manage_customers">
                            <Suspense fallback={<ModuleLoader />}>
                              <CustomersModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Purchases routes */}
                      <Route path="/purchases/*" element={
                        <Layout>
                          <ProtectedRoute permission="manage_purchases">
                            <Suspense fallback={<ModuleLoader />}>
                              <PurchasesModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      
                      {/* Transfers routes */}
                      <Route path="/transfers" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <TransfersModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Inventory Transfers route */}
                      <Route path="/inventory/transfers" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <TransfersModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Reports routes */}
                      <Route path="/reports" element={
                        <Layout>
                          <ProtectedRoute permission="view_reports">
                            <Suspense fallback={<ModuleLoader />}>
                              <ReportsHub />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      <Route path="/reports/sales" element={
                        <Layout>
                          <ProtectedRoute permission="view_reports">
                            <Suspense fallback={<ModuleLoader />}>
                              <ReportsSalesModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      <Route path="/reports/inventory" element={
                        <Layout>
                          <ProtectedRoute permission="view_reports">
                            <Suspense fallback={<ModuleLoader />}>
                              <ReportsInventoryModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      <Route path="/reports/purchases" element={
                        <Layout>
                          <ProtectedRoute permission="view_reports">
                            <Suspense fallback={<ModuleLoader />}>
                              <ReportsPurchasesModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      <Route path="/reports/scheduled" element={
                        <Layout>
                          <ProtectedRoute permission="view_reports">
                            <Suspense fallback={<ModuleLoader />}>
                              <ReportsScheduledModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* Accounting routes */}
                      <Route path="/accounting" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <AccountingModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/accounting/chart-accounts" element={
                        <Layout>
                          <div>Plan de Cuentas (Próximamente)</div>
                        </Layout>
                      } />
                      <Route path="/accounting/journal-entries" element={
                        <Layout>
                          <div>Asientos Contables (Próximamente)</div>
                        </Layout>
                      } />
                      <Route path="/accounting/bank-reconciliation" element={
                        <Layout>
                          <div>Conciliaciones Bancarias (Próximamente)</div>
                        </Layout>
                      } />
                      <Route path="/accounting/financial-reports" element={
                        <Layout>
                          <div>Reportes Financieros (Próximamente)</div>
                        </Layout>
                      } />
                      <Route path="/accounting/templates" element={
                        <Layout>
                          <div>Plantillas Contables (Próximamente)</div>
                        </Layout>
                      } />
                      <Route path="/accounting/preliminary-documents" element={
                        <Layout>
                          <div>Documentos Preliminares (Próximamente)</div>
                        </Layout>
                      } />
                      
                      
                      {/* HR routes */}
                      <Route path="/hr/*" element={
                        <Layout>
                          <ProtectedRoute permission="manage_users">
                            <Suspense fallback={<ModuleLoader />}>
                              <HumanResourcesModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                        {/* Tasks routes */}
                        <Route path="/tasks/*" element={
                          <Layout>
                            <ProtectedRoute permission="manage_tasks">
                              <Suspense fallback={<ModuleLoader />}>
                                <TasksModule />
                              </Suspense>
                            </ProtectedRoute>
                          </Layout>
                        } />
                       
                       {/* Service Tech routes */}
                       <Route path="/service-tech" element={
                         <Layout>
                           <ProtectedRoute permission="manage_services">
                             <Suspense fallback={<ModuleLoader />}>
                               <ServiceTechModule />
                             </Suspense>
                           </ProtectedRoute>
                         </Layout>
                       } />
                       
                       {/* Site Map route */}
                       <Route path="/site-map" element={
                        <Layout>
                          <Suspense fallback={<ModuleLoader />}>
                            <SiteMapModule />
                          </Suspense>
                        </Layout>
                      } />
                      
                      {/* Consignment Dropshipping routes */}
                      <Route path="/consignment-dropshipping" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ConsignmentDropshippingModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/consignment" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ConsignmentManagementView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/consignment-reception" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ConsignmentReceptionView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/consignment-stock" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ConsignmentStockView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/consignment-movements" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ConsignmentMovementsView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/consignment-reconciliation" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ConsignmentReconciliationView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/dropshipping" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <DropshippingDashboardView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/dropshipping-suppliers" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <DropshippingSuppliersView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/dropshipping-products" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <DropshippingProductsView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/dropshipping-orders" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <DropshippingOrdersView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/dropshipping-configuration" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <DropshippingConfigurationManager />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/dropshipping-movements" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <DropshippingProviderMovements />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/automatic-replenishment" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <AutomaticReplenishmentSystem />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/inter-company-flow" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <InterCompanyConsignmentFlow />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/consignment-dropshipping/obsolescence-analysis" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ConsignmentObsolescenceAnalysis />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      <Route path="/consignment-dropshipping/smart-dashboard" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <SmartDashboard />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      <Route path="/consignment-dropshipping/stock-distribution" element={
                        <Layout>
                          <ProtectedRoute permission="manage_inventory">
                            <Suspense fallback={<ModuleLoader />}>
                              <ProviderStockDistributionView />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      
                      {/* B2B Showroom routes */}
                      <Route path="/b2b-showroom" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <B2BShowroomModule />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/b2b-showroom/management" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <Suspense fallback={<ModuleLoader />}>
                              <ShowroomManagement />
                            </Suspense>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/b2b-showroom/products" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <div className="p-6">
                              <Suspense fallback={<ModuleLoader />}>
                                <ShowroomProductCatalog 
                                  showroomId="default-showroom" 
                                  companyId="default-company"
                                />
                              </Suspense>
                            </div>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/b2b-showroom/customers" element={
                        <Layout>
                          <ProtectedRoute permission="manage_sales">
                            <div className="p-6">
                              <Suspense fallback={<ModuleLoader />}>
                                <ShowroomCustomerAccess />
                              </Suspense>
                            </div>
                          </ProtectedRoute>
                        </Layout>
                      } />
                      <Route path="/b2b-showroom/carts" element={
                        <Layout>
                          <div>Carritos de Compra (Próximamente)</div>
                        </Layout>
                      } />
                      <Route path="/b2b-showroom/orders" element={
                        <Layout>
                          <div>Órdenes B2B (Próximamente)</div>
                        </Layout>
                      } />
                      <Route path="/b2b-showroom/analytics" element={
                        <Layout>
                          <div>Analíticas (Próximamente)</div>
                        </Layout>
                      } />
                      
                      {/* Implementation routes */}
                      <Route path="/implementation" element={
                        <Layout>
                          <Suspense fallback={<ModuleLoader />}>
                            <ImplementationModule />
                          </Suspense>
                        </Layout>
                      } />
                      
                      {/* Help routes */}
                      <Route path="/help" element={
                        <Layout>
                          <Suspense fallback={<ModuleLoader />}>
                            <HelpModule />
                          </Suspense>
                        </Layout>
                      } />
                      
                      {/* User Manual route */}
                      <Route path="/user-manual" element={
                        <Layout>
                          <Suspense fallback={<ModuleLoader />}>
                            <UserManualModule />
                          </Suspense>
                        </Layout>
                      } />
                      
                      {/* Corporate Catalog route */}
                      <Route path="/corporate-catalog" element={
                        <Layout>
                          <Suspense fallback={<ModuleLoader />}>
                            <CorporateCatalogDemo />
                          </Suspense>
                        </Layout>
                      } />
                      
                      {/* Design System route */}
                      <Route path="/design-system" element={
                        <Layout>
                          <Suspense fallback={<ModuleLoader />}>
                            <DesignSystemDemo />
                          </Suspense>
                        </Layout>
                      } />
                      
                       {/* System Performance Analyzer route */}
                       <Route path="/system-performance" element={
                         <Layout>
                           <ProtectedRoute permission="manage_configuration">
                             <Suspense fallback={<ModuleLoader />}>
                               <SystemPerformanceAnalyzer />
                             </Suspense>
                           </ProtectedRoute>
                         </Layout>
                       } />
                       
                        {/* Client Support Tickets */}
                        <Route path="/support-tickets" element={
                          <Layout>
                            <Suspense fallback={<ModuleLoader />}>
                              <ClientSupportTickets />
                            </Suspense>
                          </Layout>
                        } />
                        
                        {/* Customer Cases Management */}
                        <Route path="/customer-cases" element={
                          <Layout>
                            <Suspense fallback={<ModuleLoader />}>
                              <CustomerCasesPage />
                            </Suspense>
                          </Layout>
                        } />
                       
                       {/* 404 route */}
                      <Route path="*" element={
                        <Layout>
                          <NotFound />
                        </Layout>
                      } />
                      </Routes>
                      </PasswordChangeGuard>
                     </CompanySecurityWrapper>
                   </BrowserRouter>
                 </TooltipProvider>
              </DataProvider>
                </BusinessOperationsProvider>
              </I18nProvider>
            </TenantConfigProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;